import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from '../check/Check'
import { SubmitCheckFormValues, SubmitCheckResult } from '../submitCheckModal/SubmitCheckModal.types'
import { SubmitCheckModal } from '../submitCheckModal/SubmitCheckModal'
import { useCreateSubmitCheckHelpers } from '../../hooks/useCreateSubmitCheckHelpers'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQuery } from '@percent/utility'
import { AdyenSpecificCheckContainerProps } from './AdyenSpecificCheckContainer.types'
import {
  CheckType,
  MonetaryVerificationCheck
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'

export function AdyenSpecificCheckContainer({
  monetaryVerification,
  organisationName,
  partnerName,
  validationSubmission
}: Readonly<AdyenSpecificCheckContainerProps>) {
  const { monetaryVerificationsService } = useServices()
  const { t } = useTranslation()

  const [checkToSubmit, setCheckToSubmit] = useState<MonetaryVerificationCheck | null>(null)
  const [submitCheckStatus, setSubmitCheckStatus] = useState<SubmitCheckResult | null>(null)

  const { data, isLoading, errorMessage } = useQuery(
    ['v1/admin/partner-specific-checks/adyen-specific-checks', monetaryVerification.id],
    monetaryVerificationsService.getAdyenSpecificCheck,
    { verificationId: monetaryVerification.id },
    { enabled: !!monetaryVerification.id }
  )

  const { createRequestPayload, invalidateQueryKeys } = useCreateSubmitCheckHelpers(
    monetaryVerification,
    data?.status,
    validationSubmission
  )

  const [{ isPending }, { apiFunc: submitAdyenSpecificCheck }] = useMutation(
    monetaryVerificationsService.submitAdyenSpecificCheck,
    () => {
      setSubmitCheckStatus(SubmitCheckResult.SUCCESS)
    },
    () => setSubmitCheckStatus(SubmitCheckResult.ERROR)
  )

  const handleSubmit = (formData: SubmitCheckFormValues) => {
    submitAdyenSpecificCheck(createRequestPayload(formData))
  }

  const handleCloseModal = () => {
    setCheckToSubmit(null)
    setSubmitCheckStatus(null)

    if (submitCheckStatus === SubmitCheckResult.SUCCESS) {
      invalidateQueryKeys(['v1/admin/partner-specific-checks/adyen-specific-checks', monetaryVerification?.id])
    }
  }

  const checkTitle = t('typography.adyenSpecificCheck')

  return (
    <>
      <Check
        check={data ? { ...data, type: CheckType.ADYEN_SPECIFIC_CHECK } : undefined}
        monetaryVerification={monetaryVerification}
        onCheckToSubmit={setCheckToSubmit}
        title={checkTitle}
        isLoading={isLoading}
        isError={!!errorMessage}
      />
      {checkToSubmit && data && (
        <SubmitCheckModal
          isOpen={!!checkToSubmit}
          onClose={handleCloseModal}
          partnerName={partnerName}
          organisationName={organisationName}
          onSubmit={handleSubmit}
          isPending={isPending}
          submitCheckStatus={submitCheckStatus}
          checkType={CheckType.ADYEN_SPECIFIC_CHECK}
          checkStatus={data?.status}
          checkTitle={checkTitle}
        />
      )}
    </>
  )
}
